import { useEffect, useId, useMemo, useState } from 'react';
import {
  BarChart3,
  ChevronDown,
  ClipboardList,
  Package,
  Percent,
  Receipt,
  RotateCcw,
  Scale,
  Search,
  ShoppingCart,
  Sparkles,
  Target,
  TrendingDown,
  Truck,
  Users,
  Wallet,
  Wine,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useCurrentShopDetail } from '@/hooks/queries/useCurrentShopDetail';
import { usePermissions } from '@/hooks/usePermissions';
import { cn } from '@/lib/utils';

interface ChatPromptChipsProps {
  onSelect: (prompt: string) => void;
  compact?: boolean;
  mobileCollapsible?: boolean;
  mobileHorizontal?: boolean;
  defaultExpanded?: boolean;
  showOnMobile?: boolean;
}

interface PromptItem {
  key: string;
  value: string;
  icon: LucideIcon;
}

interface PromptGroup {
  id: string;
  labelKey: string;
  labelDefault: string;
  tone: 'sales' | 'acc' | 'bar' | 'ops';
  requiresBar?: boolean;
  /** Réservé owner/manager (analyses stratégiques, exports comptables). */
  requiresManagement?: boolean;
  /** Affiché uniquement aux rôles opérationnels (caissier, employé, stockiste). */
  opsOnly?: boolean;
  items: PromptItem[];
}

const TONE_CLASSES: Record<PromptGroup['tone'], string> = {
  sales: 'bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300',
  acc: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300',
  bar: 'bg-lime-100 text-lime-700 dark:bg-lime-500/15 dark:text-lime-300',
  ops: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300',
};

const PROMPT_GROUPS: PromptGroup[] = [
  {
    id: 'daily',
    labelKey: 'chat_group_daily',
    labelDefault: 'Au quotidien',
    tone: 'sales',
    items: [
      {
        key: 'chat_prompt_sales_today',
        value: 'Resume mes ventes du jour avec un conseil actionnable.',
        icon: BarChart3,
      },
      {
        key: 'chat_prompt_stock_alerts',
        value: 'Quels produits sont en rupture ou en alerte stock aujourd hui ?',
        icon: Package,
      },
      {
        key: 'chat_prompt_top_customers',
        value: 'Qui sont mes meilleurs clients cette semaine ?',
        icon: Users,
      },
      {
        key: 'chat_prompt_next_action',
        value: 'Donne moi 3 actions prioritaires a faire aujourd hui pour améliorer ma boutique.',
        icon: Target,
      },
    ],
  },
  {
    id: 'ops',
    labelKey: 'chat_group_ops',
    labelDefault: 'Au comptoir',
    tone: 'ops',
    opsOnly: true,
    items: [
      {
        key: 'chat_prompt_ops_sell',
        value: 'Je veux encaisser une vente. Guide-moi.',
        icon: ShoppingCart,
      },
      {
        key: 'chat_prompt_ops_find_sale',
        value: 'Retrouve une vente : je te donne le numéro de facture ou le nom du client.',
        icon: Search,
      },
      {
        key: 'chat_prompt_ops_return',
        value: 'Un client veut retourner un produit. Guide-moi étape par étape.',
        icon: RotateCcw,
      },
      {
        key: 'chat_prompt_ops_deliveries',
        value: "Quelles livraisons sont prévues aujourd'hui ?",
        icon: Truck,
      },
    ],
  },
  {
    id: 'accounting',
    labelKey: 'chat_group_accounting',
    labelDefault: 'Comptabilité',
    tone: 'acc',
    requiresManagement: true,
    items: [
      {
        key: 'chat_prompt_acc_sales_journal',
        value: 'Génère le journal des ventes de ce mois en Excel (format OHADA).',
        icon: ClipboardList,
      },
      {
        key: 'chat_prompt_acc_cashbook',
        value: 'Génère mon livre de caisse (compte 571) de ce mois.',
        icon: Wallet,
      },
      {
        key: 'chat_prompt_acc_receivables',
        value: 'Fais le suivi de mes créances clients (compte 411) avec leur ancienneté.',
        icon: Receipt,
      },
      {
        key: 'chat_prompt_acc_monthly_summary',
        value: 'Prépare la synthèse comptable mensuelle de ma boutique.',
        icon: BarChart3,
      },
    ],
  },
  {
    id: 'bar',
    labelKey: 'chat_group_bar',
    labelDefault: 'Bar',
    tone: 'bar',
    requiresBar: true,
    items: [
      {
        key: 'chat_prompt_bar_pour_cost',
        value: 'Quel est mon pour cost ce mois ?',
        icon: Percent,
      },
      {
        key: 'chat_prompt_bar_losses',
        value: "Où sont mes pertes / quels ingrédients ont le plus d'écart ?",
        icon: TrendingDown,
      },
      {
        key: 'chat_prompt_bar_recipe_margin',
        value: 'Quelles recettes sont les plus rentables ?',
        icon: Wine,
      },
      {
        key: 'chat_prompt_bar_cash_variance',
        value: "Quel est l'écart de caisse aujourd'hui ?",
        icon: Scale,
      },
    ],
  },
];

export function ChatPromptChips({
  onSelect,
  compact: _compact = false,
  mobileCollapsible = false,
  mobileHorizontal = false,
  defaultExpanded = true,
  showOnMobile = true,
}: ChatPromptChipsProps) {
  const { t } = useTranslation('dashboard');
  const isMobile = useIsMobile();
  const contentId = useId();
  const isCollapsible = isMobile && mobileCollapsible;
  const [expanded, setExpanded] = useState(defaultExpanded);

  const { data: shopDetailResponse } = useCurrentShopDetail();
  const barModeEnabled = Boolean(shopDetailResponse?.data?.bar_mode_enabled);
  const { isOwnerOrManager } = usePermissions();

  const groups = useMemo(
    () =>
      PROMPT_GROUPS.filter((group) => {
        if (group.requiresBar && !barModeEnabled) return false;
        if (group.requiresManagement && !isOwnerOrManager) return false;
        if (group.opsOnly && isOwnerOrManager) return false;
        return true;
      }),
    [barModeEnabled, isOwnerOrManager],
  );

  const flatPrompts = useMemo(() => groups.flatMap((group) => group.items), [groups]);

  useEffect(() => {
    if (isCollapsible) {
      setExpanded(defaultExpanded);
      return;
    }

    setExpanded(true);
  }, [defaultExpanded, isCollapsible]);

  if (isMobile && !showOnMobile) {
    return null;
  }

  function handleSelect(prompt: string) {
    onSelect(prompt);
    if (isCollapsible) {
      setExpanded(false);
    }
  }

  // Mode horizontal scrollable pour mobile immersif
  if (isMobile && mobileHorizontal) {
    return (
      <div
        className="overflow-x-auto pb-2 [scroll-snap-type:x_mandatory] [-webkit-overflow-scrolling:touch]"
        style={{ scrollbarWidth: 'none' }}
      >
        <motion.div
          className="flex gap-2 px-4 pr-8"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.05 } },
          }}
        >
          {flatPrompts.map((item) => {
            const Icon = item.icon;
            return (
              <motion.button
                key={item.key}
                type="button"
                onClick={() => handleSelect(item.value)}
                className={cn(
                  'flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full border-0 bg-white dark:bg-charcoal-800 px-3.5 py-2 text-sm text-slate-800 dark:text-slate-100 shadow-2xs',
                  '[scroll-snap-align:start]',
                  'hover:bg-slate-100 dark:hover:bg-charcoal-700 hover:shadow-xs active:scale-95 transition-all',
                )}
                variants={{
                  hidden: { opacity: 0, y: 8 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.2 } },
                }}
              >
                <Icon className="size-4 text-primary" aria-hidden />
                {t(item.key)}
              </motion.button>
            );
          })}
        </motion.div>
      </div>
    );
  }

  const groupsView = (
    <div className="space-y-3.5">
      {groups.map((group) => (
        <div key={group.id} className="space-y-2">
          <p className="px-0.5 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-charcoal-400">
            {t(group.labelKey, { defaultValue: group.labelDefault })}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {group.items.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => handleSelect(item.value)}
                  className="group flex items-center gap-2.5 rounded-[20px] border-0 bg-white dark:bg-charcoal-800 p-3 text-left transition-all shadow-2xs hover:shadow-md hover:bg-slate-50 dark:hover:bg-charcoal-700 active:scale-[0.99]"
                >
                  <span
                    className={cn(
                      'flex size-8 shrink-0 items-center justify-center rounded-lg',
                      TONE_CLASSES[group.tone],
                    )}
                  >
                    <Icon className="size-4" aria-hidden />
                  </span>
                  <span className="text-[13px] font-medium leading-snug text-charcoal-800 dark:text-charcoal-100">
                    {t(item.key)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="px-3 py-2.5">
      {isCollapsible ? (
        <>
          <button
            type="button"
            className="flex w-full items-center justify-between rounded-md px-1 py-1 text-left text-xs font-semibold text-charcoal-800"
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
            aria-controls={contentId}
          >
            <span className="inline-flex items-center gap-1.5">
              <Sparkles className="size-3.5 text-primary" />
              {t('chat_suggestions')}
            </span>
            <ChevronDown className={cn('size-4 transition-transform', expanded && 'rotate-180')} />
          </button>

          {expanded && (
            <div id={contentId} className="mt-3">
              {groupsView}
            </div>
          )}
        </>
      ) : (
        <>
          <p className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium text-charcoal-700 dark:text-charcoal-200">
            <Sparkles className="size-3.5 text-primary" />
            {t('chat_suggestions')}
          </p>
          {groupsView}
        </>
      )}
    </div>
  );
}
